import { useState, useEffect, useCallback, useRef } from 'react';
import { recordsService } from '../../services/recordsService';
import type { PatientDocument } from '../../types/records';

interface PatientDocumentsPanelProps {
  patientId: string;
}

// ── Document categories with French labels + icons ──
const DOCUMENT_CATEGORIES = [
  { value: 'xray',         label: 'Radio',         icon: '☢️' },
  { value: 'panoramic',    label: 'Panoramique',   icon: '🦷' },
  { value: 'cbct',         label: 'CBCT / 3D',     icon: '🔬' },
  { value: 'photo',        label: 'Photo',         icon: '📸' },
  { value: 'lab',          label: 'Labo',          icon: '🧪' },
  { value: 'consent',      label: 'Consentement',  icon: '📝' },
  { value: 'prescription', label: 'Ordonnance',    icon: '💊' },
  { value: 'report',       label: 'Rapport',       icon: '📋' },
  { value: 'other',        label: 'Autre',         icon: '📁' },
] as const;

// ── Simplified filter groups (fewer chips) ──
const FILTER_GROUPS = [
  { label: '📷 Imagerie',   values: ['xray', 'panoramic', 'cbct', 'photo'] },
  { label: '📋 Documents',  values: ['consent', 'prescription', 'report'] },
  { label: '🧪 Labo',       values: ['lab'] },
  { label: '📁 Autre',      values: ['other'] },
] as const;

const CATEGORY_COLORS: Record<string, string> = {
  xray:         'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border-yellow-500/20',
  panoramic:    'bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border-cyan-500/20',
  cbct:         'bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/20',
  photo:        'bg-pink-500/10 text-pink-600 dark:text-pink-400 border-pink-500/20',
  lab:          'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
  consent:      'bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/20',
  prescription: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
  report:       'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20',
  other:        'bg-muted text-muted-foreground border-border/50',
};

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

function isImageType(contentType: string): boolean {
  return contentType.startsWith('image/');
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function PatientDocumentsPanel({ patientId }: PatientDocumentsPanelProps) {
  // ── State ──
  const [documents, setDocuments] = useState<PatientDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filterCategories, setFilterCategories] = useState<string[]>([]);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [previewDoc, setPreviewDoc] = useState<PatientDocument | null>(null);

  // ── Upload form state ──
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadCategory, setUploadCategory] = useState('photo');
  const [uploadTooth, setUploadTooth] = useState('');
  const [uploadNotes, setUploadNotes] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Load documents ──
  const loadDocuments = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    setError(null);
    try {
      const docs = await recordsService.getDocuments(patientId);
      setDocuments(docs);
    } catch (err) {
      console.error('[Documents] Load error:', err);
      setError('Erreur lors du chargement des documents');
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  // ── Client-side filter ──
  const filteredDocuments = filterCategories.length === 0
    ? documents
    : documents.filter(doc => filterCategories.includes(doc.category));

  // ── Upload handler ──
  const handleUpload = useCallback(async () => {
    if (!uploadFile || !patientId) return;
    setUploading(true);
    setError(null);
    try {
      const result = await recordsService.uploadDocument({
        patient_id: patientId,
        file: uploadFile,
        category: uploadCategory,
        tooth_number: uploadTooth ? parseInt(uploadTooth, 10) : undefined,
        notes: uploadNotes || undefined,
      });
      if (result.success) {
        setUploadFile(null);
        setUploadNotes('');
        setUploadTooth('');
        setShowUploadForm(false);
        await loadDocuments();
      } else {
        setError(result.message || 'Échec du téléchargement');
      }
    } catch (err) {
      console.error('[Documents] Upload error:', err);
      setError('Erreur lors du téléchargement');
    } finally {
      setUploading(false);
    }
  }, [uploadFile, patientId, uploadCategory, uploadTooth, uploadNotes, loadDocuments]);

  // ── Delete handler ──
  const handleDelete = useCallback(async (doc: PatientDocument) => {
    if (!confirm(`Supprimer "${doc.file_name}" ?`)) return;
    try {
      const result = await recordsService.deleteDocument(doc.id);
      if (result.success) {
        await loadDocuments();
        if (previewDoc?.id === doc.id) setPreviewDoc(null);
      } else {
        setError(result.message || 'Échec de la suppression');
      }
    } catch (err) {
      console.error('[Documents] Delete error:', err);
      setError('Erreur lors de la suppression');
    }
  }, [loadDocuments, previewDoc]);

  // ── Download handler ──
  const handleDownload = useCallback(async (doc: PatientDocument) => {
    try {
      await recordsService.downloadDocument(doc.id);
    } catch (err) {
      console.error('[Documents] Download error:', err);
      setError('Erreur lors du téléchargement');
    }
  }, []);

  // ── Drag & drop handlers ──
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      setUploadFile(files[0]);
      setShowUploadForm(true);
      // Auto-detect category from content type
      const type = files[0].type;
      if (type.startsWith('image/')) {
        setUploadCategory('photo');
      } else if (type === 'application/pdf') {
        setUploadCategory('report');
      }
    }
  }, []);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      setUploadFile(files[0]);
      setShowUploadForm(true);
      const type = files[0].type;
      if (type.startsWith('image/')) {
        setUploadCategory('photo');
      } else if (type === 'application/pdf') {
        setUploadCategory('report');
      }
    }
  }, []);

  const categoryInfo = (cat: string) => DOCUMENT_CATEGORIES.find(c => c.value === cat);

  return (
    <div className="flex flex-col h-full">
      {/* ── Header ── */}
      <div className="shrink-0 px-3 pt-3 pb-2">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-[11px] font-semibold uppercase tracking-wider text-foreground/80">
            Documents
          </h3>
          <div className="flex items-center gap-1">
            <span className="text-[10px] text-muted-foreground tabular-nums">
              {documents.length}
            </span>
            <button
              onClick={() => { setShowUploadForm(!showUploadForm); setUploadFile(null); }}
              className="ml-1 p-1 rounded-md bg-primary/10 hover:bg-primary/20 text-primary transition-colors"
              title="Ajouter un document"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </div>
        </div>

        {/* Category filter chips — simplified groups */}
        <div className="flex flex-wrap gap-1">
          <button
            onClick={() => setFilterCategories([])}
            className={`px-2 py-0.5 rounded-full text-[9px] font-medium border transition-colors ${
              filterCategories.length === 0
                ? 'bg-primary/15 text-primary border-primary/30'
                : 'bg-muted/40 text-muted-foreground border-border/30 hover:bg-muted/60'
            }`}
          >
            Tous
          </button>
          {FILTER_GROUPS.map(group => {
            const isActive = group.values.every(v => filterCategories.includes(v))
              && filterCategories.length === group.values.length;
            return (
              <button
                key={group.label}
                onClick={() => setFilterCategories(isActive ? [] : [...group.values])}
                className={`px-2 py-0.5 rounded-full text-[9px] font-medium border transition-colors ${
                  isActive
                    ? 'bg-primary/15 text-primary border-primary/30'
                    : 'bg-muted/40 text-muted-foreground border-border/30 hover:bg-muted/60'
                }`}
              >
                {group.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Error ── */}
      {error && (
        <div className="shrink-0 mx-3 mb-2 px-2.5 py-1.5 bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-800/50 rounded-lg flex items-center gap-2">
          <svg className="h-3 w-3 text-red-500 dark:text-red-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01" />
          </svg>
          <span className="text-[10px] text-red-700 dark:text-red-300 flex-1">{error}</span>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
            <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* ── Document List: scrollable area ── */}
      <div className="flex-1 overflow-y-auto px-0 scrollbar-thin">

      {/* ── Document List ── */}
      <div className="px-3 pb-3">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <svg className="h-5 w-5 animate-spin text-primary" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          </div>
        ) : filteredDocuments.length === 0 ? (
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`flex flex-col items-center justify-center py-10 text-center transition-colors rounded-xl ${
              dragOver ? 'bg-primary/10 border-2 border-dashed border-primary' : ''
            }`}
          >
            <div className="w-12 h-12 rounded-xl bg-muted/60 border border-border/30 flex items-center justify-center mb-3">
              <svg className="w-6 h-6 text-muted-foreground/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <p className="text-[11px] text-muted-foreground font-medium mb-1">
              Aucun document
            </p>
            <p className="text-[10px] text-muted-foreground/60 max-w-[200px]">
              Glissez des radios, photos ou documents ici pour les ajouter au dossier
            </p>
          </div>
        ) : (
          <div className="space-y-1.5 pt-1">
            {filteredDocuments.map(doc => {
              const cat = categoryInfo(doc.category);
              const catColor = CATEGORY_COLORS[doc.category] || CATEGORY_COLORS.other;
              const isImage = isImageType(doc.content_type);

              return (
                <div
                  key={doc.id}
                  className="group relative flex items-start gap-2.5 p-2 rounded-xl border border-border/30 bg-muted/20 
                    hover:bg-muted/40 hover:border-border/50 transition-all cursor-pointer"
                  onClick={() => isImage ? setPreviewDoc(doc) : handleDownload(doc)}
                >
                  {/* Thumbnail / Icon */}
                  <div className="w-10 h-10 rounded-lg overflow-hidden bg-muted border border-border/40 shrink-0 flex items-center justify-center">
                    {isImage ? (
                      <img
                        src={`/api/records/api/document/${doc.id}/download/`}
                        alt={doc.file_name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        onError={(e) => {
                          // Fallback to icon on load error
                          const img = e.target as HTMLImageElement;
                          img.style.display = 'none';
                          img.parentElement!.innerHTML = '<svg class="w-5 h-5 text-pink-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>';
                        }}
                      />
                    ) : doc.content_type === 'application/pdf' ? (
                      <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5 text-muted-foreground/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    )}
                  </div>

                  {/* Info */}
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] font-medium text-foreground truncate leading-tight">
                      {doc.file_name}
                    </p>
                    <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                      <span className={`inline-flex items-center gap-0.5 px-1.5 py-px rounded-full text-[9px] font-medium border ${catColor}`}>
                        {cat?.icon} {cat?.label || doc.category}
                      </span>
                      {doc.tooth_number && (
                        <span className="text-[9px] font-mono px-1 py-px rounded bg-muted border border-border/30 text-muted-foreground">
                          #{doc.tooth_number}
                        </span>
                      )}
                      <span className="text-[9px] text-muted-foreground/60">
                        {formatFileSize(doc.file_size)}
                      </span>
                    </div>
                    {doc.notes && (
                      <p className="text-[9px] text-muted-foreground/70 mt-0.5 truncate italic">
                        {doc.notes}
                      </p>
                    )}
                    <p className="text-[9px] text-muted-foreground/50 mt-0.5">
                      {formatDate(doc.created_at)}
                    </p>
                  </div>

                  {/* Actions (hover) */}
                  <div className="absolute right-1.5 top-1.5 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDownload(doc); }}
                      className="p-1 rounded-md hover:bg-primary/10 text-primary transition-colors"
                      title="Télécharger"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(doc); }}
                      className="p-1 rounded-md hover:bg-red-500/10 text-red-400 hover:text-red-500 transition-colors"
                      title="Supprimer"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      </div>

      {/* ── Image Preview Modal ── */}
      {previewDoc && isImageType(previewDoc.content_type) && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm"
          onClick={() => setPreviewDoc(null)}
        >
          <div
            className="relative max-w-3xl max-h-[80vh] m-4 rounded-2xl overflow-hidden bg-card border border-border shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            {/* Preview header */}
            <div className="flex items-center justify-between px-4 py-2.5 bg-card/95 border-b border-border/40">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-[10px] text-muted-foreground">📸</span>
                <span className="text-[12px] font-medium text-foreground truncate">{previewDoc.file_name}</span>
                {previewDoc.tooth_number && (
                  <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-muted border border-border/30 text-muted-foreground">
                    #{previewDoc.tooth_number}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0 ml-3">
                <button
                  onClick={() => handleDownload(previewDoc)}
                  className="p-1.5 rounded-md hover:bg-primary/10 text-primary transition-colors"
                  title="Télécharger"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                </button>
                <button
                  onClick={() => setPreviewDoc(null)}
                  className="p-1.5 rounded-md hover:bg-muted text-muted-foreground transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            {/* Image - we build the URL from the download endpoint */}
            <div className="flex items-center justify-center bg-black/20 p-2 min-h-[300px]">
              <img
                src={`/api/records/api/document/${previewDoc.id}/download/`}
                alt={previewDoc.file_name}
                className="max-w-full max-h-[70vh] object-contain rounded-lg"
                onError={(e) => {
                  // Fallback: try direct path
                  const img = e.target as HTMLImageElement;
                  if (!img.src.includes('/records/api/document/')) {
                    img.src = `/records/api/document/${previewDoc.id}/download/`;
                  }
                }}
              />
            </div>
            {/* Notes */}
            {previewDoc.notes && (
              <div className="px-4 py-2 bg-card/95 border-t border-border/40">
                <p className="text-[11px] text-muted-foreground italic">{previewDoc.notes}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Upload Modal ── */}
      {showUploadForm && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={() => { setShowUploadForm(false); setUploadFile(null); }}
        >
          <div
            className="relative w-full max-w-md m-4 bg-card rounded-2xl border border-border shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-border/40">
              <h3 className="text-sm font-semibold text-foreground">Ajouter un document</h3>
              <button
                onClick={() => { setShowUploadForm(false); setUploadFile(null); }}
                className="p-1.5 rounded-md hover:bg-muted text-muted-foreground transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal body */}
            <div className="p-5 space-y-3">
              {/* Drop zone */}
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`relative cursor-pointer border-2 border-dashed rounded-xl p-5 text-center transition-all ${
                  dragOver
                    ? 'border-primary bg-primary/10 scale-[1.01]'
                    : uploadFile
                      ? 'border-green-500/40 bg-green-500/5'
                      : 'border-border/60 hover:border-primary/40 hover:bg-muted/40'
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept="image/*,.pdf,.doc,.docx,.dicom"
                  onChange={handleFileSelect}
                />
                {uploadFile ? (
                  <div className="flex items-center gap-3">
                    {isImageType(uploadFile.type) ? (
                      <div className="w-12 h-12 rounded-lg overflow-hidden bg-muted border border-border/40 shrink-0">
                        <img src={URL.createObjectURL(uploadFile)} alt="" className="w-full h-full object-cover" />
                      </div>
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-muted border border-border/40 flex items-center justify-center shrink-0">
                        <svg className="w-6 h-6 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                    )}
                    <div className="min-w-0 flex-1 text-left">
                      <p className="text-[12px] font-medium text-foreground truncate">{uploadFile.name}</p>
                      <p className="text-[11px] text-muted-foreground">{formatFileSize(uploadFile.size)}</p>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); setUploadFile(null); }}
                      className="p-1 rounded-md hover:bg-muted text-muted-foreground"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <>
                    <svg className="mx-auto w-8 h-8 text-muted-foreground/60 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <p className="text-[12px] text-muted-foreground">
                      Glissez un fichier ici ou <span className="text-primary font-medium">parcourir</span>
                    </p>
                    <p className="text-[10px] text-muted-foreground/60 mt-1">
                      Images, PDF, DICOM — max 20 Mo
                    </p>
                  </>
                )}
              </div>

              {/* Category selector */}
              <div>
                <label className="text-[11px] font-medium text-muted-foreground mb-1.5 block">Catégorie</label>
                <div className="flex flex-wrap gap-1">
                  {DOCUMENT_CATEGORIES.map(cat => (
                    <button
                      key={cat.value}
                      type="button"
                      onClick={() => setUploadCategory(cat.value)}
                      className={`px-2 py-1 rounded-lg text-[10px] font-medium border transition-all ${
                        uploadCategory === cat.value
                          ? `${CATEGORY_COLORS[cat.value]} ring-1 ring-current/20`
                          : 'bg-muted/40 text-muted-foreground border-border/30 hover:bg-muted/60'
                      }`}
                    >
                      {cat.icon} {cat.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Optional fields */}
              <div>
                <label className="text-[11px] font-medium text-muted-foreground mb-1.5 block">N° dent (optionnel)</label>
                <input
                  type="number"
                  min={11}
                  max={48}
                  value={uploadTooth}
                  onChange={e => setUploadTooth(e.target.value)}
                  placeholder="ex: 14"
                  className="w-full px-3 py-1.5 text-[12px] bg-muted/60 border border-border/40 rounded-lg
                    focus:outline-none focus:ring-1 focus:ring-primary/30 focus:border-primary/40
                    text-foreground placeholder-muted-foreground/50"
                />
              </div>

              <div>
                <label className="text-[11px] font-medium text-muted-foreground mb-1.5 block">Notes (optionnel)</label>
                <textarea
                  value={uploadNotes}
                  onChange={e => setUploadNotes(e.target.value)}
                  placeholder="Avant extraction, contrôle post-op..."
                  rows={2}
                  className="w-full px-3 py-1.5 text-[12px] bg-muted/60 border border-border/40 rounded-lg resize-none
                    focus:outline-none focus:ring-1 focus:ring-primary/30 focus:border-primary/40
                    text-foreground placeholder-muted-foreground/50"
                />
              </div>
            </div>

            {/* Modal footer */}
            <div className="flex items-center gap-2 px-5 py-3 border-t border-border/40 bg-muted/20 rounded-b-2xl">
              <button
                onClick={handleUpload}
                disabled={!uploadFile || uploading}
                className={`flex-1 px-4 py-2 text-[12px] font-semibold rounded-lg transition-all ${
                  uploadFile && !uploading
                    ? 'bg-primary hover:bg-primary/90 text-primary-foreground shadow-sm shadow-primary/20 active:scale-[0.98]'
                    : 'bg-muted text-muted-foreground cursor-not-allowed'
                }`}
              >
                {uploading ? (
                  <span className="flex items-center justify-center gap-1.5">
                    <svg className="h-3.5 w-3.5 animate-spin" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Envoi en cours...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-1.5">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                    Envoyer
                  </span>
                )}
              </button>
              <button
                onClick={() => { setShowUploadForm(false); setUploadFile(null); }}
                className="px-4 py-2 text-[12px] font-medium text-muted-foreground hover:text-foreground bg-muted/40 hover:bg-muted/60 border border-border/30 rounded-lg transition-colors"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
